import { getAuthHeaders } from "./auth";

export interface ChatSession {
  sessionId: string;
  status: string;
  createdAt: string;
}

export interface ChatMessage {
  _id: string;
  role: "user" | "assistant" | "system";
  content: string;
  intent?: any;
  payload?: any;
  metadata?: any;
  createdAt: string;
}

// Development bypass: Direct backend call to avoid Vite proxy issues
const USE_DIRECT_BACKEND = import.meta.env.DEV;
const BACKEND_URL = "https://localhost:4000";

export async function createSession(parentId?: string): Promise<ChatSession> {
  const body: Record<string, unknown> = {};
  if (parentId && typeof parentId === 'string') {
    body.parentId = parentId;
  }

  const url = USE_DIRECT_BACKEND ? `${BACKEND_URL}/api/chat/sessions` : "/api/chat/sessions";
  console.log('[createSession] Calling:', url, 'Direct backend:', USE_DIRECT_BACKEND);

  const res = await fetch(url, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create session: ${text}`);
  }
  return res.json();
}

export async function sendMessage(sessionId: string, message: string): Promise<{ messages: ChatMessage[]; suggestedActions?: string[] }> {
  const url = USE_DIRECT_BACKEND
    ? `${BACKEND_URL}/api/chat/sessions/${sessionId}/messages`
    : `/api/chat/sessions/${sessionId}/messages`;

  const res = await fetch(url, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message })
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to send message: ${text}`);
  }
  return res.json();
}

export async function listMessages(sessionId: string): Promise<ChatMessage[]> {
  const url = USE_DIRECT_BACKEND
    ? `${BACKEND_URL}/api/chat/sessions/${sessionId}/messages`
    : `/api/chat/sessions/${sessionId}/messages`;

  const res = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: "include"
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to list messages: ${text}`);
  }
  return res.json();
}

export async function getAllUserQuestions(): Promise<string[]> {
  const url = USE_DIRECT_BACKEND
    ? `${BACKEND_URL}/api/chat/history/questions`
    : "/api/chat/history/questions";

  const res = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: "include"
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to get user questions: ${text}`);
  }
  const data = await res.json();
  return data.questions || [];
}

export interface RecommendedActionsContext {
  sessionId?: string;
  currentRoute?: string;
  recentMessages?: string[];
}

export interface RecommendedAction {
  text: string;
  isApp?: boolean;
}

export async function getRecommendedActions(context: RecommendedActionsContext): Promise<RecommendedAction[]> {
  const url = USE_DIRECT_BACKEND
    ? `${BACKEND_URL}/api/chat/recommended-actions`
    : "/api/chat/recommended-actions";

  const res = await fetch(url, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(context)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to get recommended actions: ${text}`);
  }
  const data = await res.json();
  const rawActions = data.actions || [];

  // Normalize actions to RecommendedAction format
  return rawActions.map((action: string | { text: string; isApp: boolean }) => {
    if (typeof action === 'string') {
      return { text: action, isApp: false };
    }
    return action;
  });
}
